import { useState, useEffect, useRef, useMemo } from 'preact/hooks'
import { useForm, type UseFormRegister, type FieldErrors } from 'react-hook-form'
import { X, Loader2, AlertCircle, Plus, Trash2, Copy } from 'lucide-preact'
import { AUTH_TYPE } from '@/types/authConnections'
import type { AuthenticationType, AddAuthConnectionRequest, GoogleWorkspaceCredentials, MicrosoftCredentials } from '@/types/authConnections'
import { getDisplayErrorMessages } from '@/utils/errorHandling'

/**
 * One form shape covering both credential-bearing methods. Google Workspace uses `domain` as its
 * single primary domain; Microsoft uses `tenantDomain` for the Entra tenant and carries its
 * sign-in domains separately in the domain list below. Only the fields for the selected method
 * are rendered or submitted.
 */
interface AuthCredentialsFormData {
  clientId: string
  clientSecret: string
  domain: string
  tenantDomain: string
}

/**
 * Where Auth0 sends the user back after the identity provider authenticates them.
 *
 * Null when VITE_AUTH0_DOMAIN is unset. Interpolating it blindly would render
 * "https://undefined/login/callback", which the customer would dutifully register on their Entra
 * app and then spend a long time debugging AADSTS50011. Better to show nothing and say why.
 */
const AUTH0_DOMAIN = (import.meta.env.VITE_AUTH0_DOMAIN as string | undefined)
  ?.trim()
  // The convention is a bare hostname — AuthContext passes this straight to createAuth0Client,
  // which expects one. But an environment configured with a scheme or a trailing slash would
  // otherwise produce "https://https://tenant.auth0.com//login/callback", and the customer would
  // register that on their Entra app. Normalizing is cheaper than the AADSTS50011 it prevents.
  .replace(/^https?:\/\//i, '')
  .replace(/\/+$/, '')

const AUTH0_CALLBACK_URL = AUTH0_DOMAIN ? `https://${AUTH0_DOMAIN}/login/callback` : null

/**
 * The "Before you begin" panel for Microsoft. Split out because it is a self-contained block of
 * setup guidance with no bearing on the form's state — keeping it inline made the modal harder to
 * read for no benefit.
 *
 * It carries the two things a customer cannot work out for themselves: the exact redirect URI to
 * register, and the Entra role needed to register it.
 */
function MicrosoftSetupGuidance({
  copied,
  onCopyCallbackUrl
}: {
  copied: boolean
  onCopyCallbackUrl: () => void
}) {
  return (
    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300 space-y-2">
      <p className="font-medium">Before you begin:</p>
      <p>
        Register an application in the Microsoft Entra admin center. From the Microsoft 365
        admin center go to <strong>All admin centers → Microsoft Entra → Microsoft Entra ID
        → App registrations</strong>. No Azure subscription is required.
      </p>
      <p>
        Whoever does the setup needs the <strong>Application Administrator</strong>,
        Cloud Application Administrator, or Global Administrator role.
      </p>
      <div>
        <p className="mb-1">Register this exact redirect URI on the app, as a <strong>Web</strong> platform:</p>
        {AUTH0_CALLBACK_URL ? (
          <>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-2 py-1 rounded bg-white/70 dark:bg-slate-900/60 text-xs break-all text-slate-800 dark:text-slate-200">
                {AUTH0_CALLBACK_URL}
              </code>
              <button
                type="button"
                onClick={onCopyCallbackUrl}
                className="px-2 py-1 rounded border border-blue-300 dark:border-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/40 flex items-center gap-1 text-xs"
              >
                <Copy className="w-3 h-3" />
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className="mt-1 text-xs">
              A mismatch in protocol, path or trailing slash fails sign-in with AADSTS50011.
            </p>
          </>
        ) : (
          // Showing "https://undefined/login/callback" would be worse than showing nothing:
          // the customer registers it, and the failure surfaces much later as AADSTS50011.
          <p className="px-2 py-1 rounded bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-300">
            The redirect URI cannot be shown — this dashboard is missing its Auth0 domain
            configuration. Ask an administrator to check <code>VITE_AUTH0_DOMAIN</code>
            before completing this setup.
          </p>
        )}
      </div>
      <a
        href="https://learn.microsoft.com/en-us/entra/identity-platform/quickstart-register-app"
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:no-underline inline-block"
      >
        View setup instructions →
      </a>
    </div>
  )
}

/** Shared input styling, so a field's error state does not need restating at every call site. */
const credentialInputClass = (hasError: boolean) =>
  `w-full px-3 py-2 rounded-lg border bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
    hasError ? 'border-red-400' : 'border-slate-300 dark:border-slate-600'
  }`

/**
 * The Google Workspace credential inputs. Extracted alongside the Microsoft equivalent so each
 * provider's fields are one unit and the modal only decides which provider is selected.
 */
function GoogleWorkspaceCredentialFields({
  register,
  errors,
  loading,
  allowedDomains
}: {
  register: UseFormRegister<AuthCredentialsFormData>
  errors: FieldErrors<AuthCredentialsFormData>
  loading: boolean
  allowedDomains: string[]
}) {
  // Identical rules whether the domain is picked from a list or typed, so they are declared once.
  const domainRules = {
    required: 'Domain is required',
    minLength: { value: 3, message: 'Domain must be at least 3 characters' },
    maxLength: { value: 253, message: 'Domain must not exceed 253 characters' },
    pattern: { value: /\./, message: 'Domain must contain a dot (.)' }
  }

  return (
    <div className="space-y-4">
      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
        <p className="font-medium mb-1">Before you begin:</p>
        <p>Create an OAuth 2.0 application in your Google Admin Console and obtain the Client ID and Secret.</p>
        <a
          href="https://support.google.com/cloud/answer/6158849"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:no-underline mt-1 inline-block"
        >
          View setup instructions →
        </a>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Client ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('clientId', {
            required: 'Client ID is required',
            minLength: { value: 10, message: 'Client ID must be at least 10 characters' },
            maxLength: { value: 500, message: 'Client ID must not exceed 500 characters' }
          })}
          disabled={loading}
          className={credentialInputClass(!!errors.clientId)}
          placeholder="123456789-abc.apps.googleusercontent.com"
        />
        {errors.clientId && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.clientId.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Client Secret <span className="text-red-500">*</span>
        </label>
        <input
          type="password"
          {...register('clientSecret', {
            required: 'Client Secret is required',
            minLength: { value: 10, message: 'Client Secret must be at least 10 characters' },
            maxLength: { value: 500, message: 'Client Secret must not exceed 500 characters' }
          })}
          disabled={loading}
          className={credentialInputClass(!!errors.clientSecret)}
          placeholder="Enter client secret"
        />
        {errors.clientSecret && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.clientSecret.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Domain <span className="text-red-500">*</span>
        </label>
        {allowedDomains.length > 1 ? (
          <select
            {...register('domain', domainRules)}
            disabled={loading}
            className={credentialInputClass(!!errors.domain)}
          >
            <option value="">Select domain</option>
            {allowedDomains.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            {...register('domain', domainRules)}
            disabled={loading || allowedDomains.length === 1}
            className={credentialInputClass(!!errors.domain)}
            placeholder="example.com"
          />
        )}
        {errors.domain && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.domain.message}</p>
        )}
      </div>
    </div>
  )
}

/**
 * The Microsoft credential inputs. Split from the modal so each provider's fields are one unit
 * rather than a long inline branch — the modal only needs to decide which provider is selected.
 */
function MicrosoftCredentialFields({
  register,
  errors,
  loading,
  copied,
  onCopyCallbackUrl
}: {
  register: UseFormRegister<AuthCredentialsFormData>
  errors: FieldErrors<AuthCredentialsFormData>
  loading: boolean
  copied: boolean
  onCopyCallbackUrl: () => void
}) {
  return (
    <div className="space-y-4">
      <MicrosoftSetupGuidance copied={copied} onCopyCallbackUrl={onCopyCallbackUrl} />

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Application (Client) ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('clientId', {
            required: 'Application (Client) ID is required',
            minLength: { value: 10, message: 'Application (Client) ID must be at least 10 characters' },
            maxLength: { value: 500, message: 'Application (Client) ID must not exceed 500 characters' }
          })}
          disabled={loading}
          className={credentialInputClass(!!errors.clientId)}
          placeholder="00000000-0000-0000-0000-000000000000"
        />
        {errors.clientId && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.clientId.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Client Secret <span className="text-red-500">*</span>
        </label>
        <input
          type="password"
          {...register('clientSecret', {
            required: 'Client Secret is required',
            minLength: { value: 10, message: 'Client Secret must be at least 10 characters' },
            maxLength: { value: 500, message: 'Client Secret must not exceed 500 characters' }
          })}
          disabled={loading}
          className={credentialInputClass(!!errors.clientSecret)}
          placeholder="Enter the client secret value"
        />
        {errors.clientSecret && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.clientSecret.message}</p>
        )}
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Microsoft shows the secret <strong>value</strong> once, when it is created. Copy the
          Value column, not the Secret ID.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Tenant Domain <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          {...register('tenantDomain', {
            required: 'Tenant domain is required',
            minLength: { value: 3, message: 'Tenant domain must be at least 3 characters' },
            maxLength: { value: 253, message: 'Tenant domain must not exceed 253 characters' },
            pattern: { value: /\./, message: 'Tenant domain must contain a dot (.)' }
          })}
          disabled={loading}
          className={credentialInputClass(!!errors.tenantDomain)}
          placeholder="contoso.onmicrosoft.com"
        />
        {errors.tenantDomain && (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.tenantDomain.message}</p>
        )}
        {/* Microsoft accepts any verified domain on the tenant here, not just the .onmicrosoft.com
            one — both resolve to the same tenant (verified against the discovery endpoint in
            ORB-632/633). The placeholder alone implies otherwise, so say so. The preference is not
            cosmetic: .onmicrosoft.com cannot be removed from a tenant, whereas a custom domain can,
            which would break this connection's authorize URL long after anyone would connect the
            failure back to this form. */}
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          This identifies the Entra tenant, not the domain people sign in with. A verified
          custom domain such as <strong>contoso.com</strong> works here too, but{' '}
          <strong>.onmicrosoft.com</strong> never changes, so prefer it if you have both.
        </p>
      </div>
    </div>
  )
}

/**
 * The shared domain chip list. Google Workspace treats it as optional aliases, Microsoft as the
 * required sign-in domains — same interaction, different label, requirement and empty state.
 */
function DomainListEditor({
  isMicrosoft,
  loading,
  domains,
  newDomain,
  onNewDomainChange,
  onAdd,
  onRemove
}: {
  isMicrosoft: boolean
  loading: boolean
  domains: string[]
  newDomain: string
  onNewDomainChange: (value: string) => void
  onAdd: () => void
  onRemove: (domain: string) => void
}) {
  return (
    <div className="space-y-3">
      {isMicrosoft && (
        // Explains what the field means; it no longer has to carry the requirement as well.
        // The "Sign-in domains *" label and its empty state below say that, and say it in
        // the place it actually applies.
        //
        // "Often different, but the same if there is no custom domain" rather than a flat "not the
        // tenant domain": a tenant with no verified custom domain signs its people in at
        // {tenant}.onmicrosoft.com, so for them the two fields legitimately hold the same value.
        // The earlier wording told that whole class of tenants their correct entry was a mistake.
        <p className="text-xs text-slate-600 dark:text-slate-400">
          The domains your people's email addresses end in — the part after the{' '}
          <strong>@</strong>. Usually different from the tenant domain above, but the same if your
          tenant has no custom domain (e.g. everyone signs in at{' '}
          <strong>contoso.onmicrosoft.com</strong>).
        </p>
      )}

      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
        {isMicrosoft ? 'Add an email domain' : 'Domain Aliases (Optional)'}
      </label>
      <div className="flex gap-2">
        <input
          type="text"
          value={newDomain}
          onInput={e => onNewDomainChange((e.target as HTMLInputElement).value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd()
            }
          }}
          disabled={loading}
          className="flex-1 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder={isMicrosoft ? 'contoso.com' : 'alias.example.com'}
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={loading || !newDomain.trim()}
          className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {/* The requirement belongs to the list, not to the input above it. An asterisk on the input
          reads as "type something here", which is wrong — the list is usually already prefilled
          from the company's allowed domains and needs nothing typed at all. */}
      <div>
        {isMicrosoft && (
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Sign-in domains <span className="text-red-500">*</span>
          </label>
        )}
        {domains.length > 0 ? (
          <div className="space-y-1">
            {domains.map(domain => (
              <div
                key={domain}
                className="flex items-center justify-between px-3 py-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700"
              >
                <span className="text-sm text-slate-700 dark:text-slate-300">{domain}</span>
                <button
                  type="button"
                  onClick={() => onRemove(domain)}
                  disabled={loading}
                  className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        ) : isMicrosoft ? (
          <div className="px-3 py-3 rounded border border-dashed border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 text-sm text-red-700 dark:text-red-400">
            No sign-in domains yet — at least one is required.
          </div>
        ) : null}
      </div>
    </div>
  )
}

interface AuthConnectionModalProps {
  isOpen: boolean
  authType: AuthenticationType | null
  typeLabel?: string
  allowedDomains?: string[]
  /**
   * Domains already routed by another connection on this organization. Excluded from the prefill,
   * because the server refuses a domain two connections both claim.
   */
  claimedDomains?: string[]
  /**
   * Adds domains to the company's allowed list. Optional: when absent the modal still refuses to
   * submit an unlisted domain, it just cannot offer to fix it here.
   */
  onAddAllowedDomains?: (domains: string[]) => Promise<void>
  onClose: () => void
  onCreate: (request: AddAuthConnectionRequest) => Promise<void>
}

export function AuthConnectionModal({
  isOpen,
  authType,
  typeLabel,
  allowedDomains = [],
  claimedDomains = [],
  onAddAllowedDomains,
  onClose,
  onCreate
}: AuthConnectionModalProps) {
  const [loading, setLoading] = useState(false)
  // A list, not one string: the API can return several validation errors at once and each has to
  // render as its own line. Holding them joined would make separation a CSS concern.
  const [errorMessages, setErrorMessages] = useState<string[] | null>(null)
  // Google Workspace treats this list as optional aliases; Microsoft treats it as the required
  // sign-in domains. Same chip UI either way, different meaning and validation.
  const [domainAliases, setDomainAliases] = useState<string[]>([])
  const [newAlias, setNewAlias] = useState('')
  const [copied, setCopied] = useState(false)

  const isGoogleWorkspace = authType === AUTH_TYPE.GoogleWorkspace
  const isMicrosoft = authType === AUTH_TYPE.Microsoft
  const requiresCredentials = isGoogleWorkspace || isMicrosoft

  // Opting in to add unlisted domains to the company. Deliberately a tick rather than automatic:
  // the allowed-domains list gates who can be invited and is unique across companies, so widening
  // it is a decision, not a side effect of configuring a connection.
  const [addUnlistedDomains, setAddUnlistedDomains] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset
  } = useForm<AuthCredentialsFormData>({
    mode: 'onChange',
    defaultValues: {
      clientId: '',
      clientSecret: '',
      domain: '',
      tenantDomain: ''
    }
  })

  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Read inside the effect without making it a dependency: re-running on a new array identity
  // would wipe whatever the user had typed, and the parent is free to pass a fresh array on
  // every render. The seed only needs the value current at the moment the provider changes.
  /**
   * Company domains that no other connection already routes.
   *
   * A domain can belong to one connection at most — Auth0's Home Realm Discovery resolves it to
   * whichever matches first, silently, so a second connection claiming it is created and then
   * never reached (ORB-632). Offering one Google Workspace already holds would hand the admin a
   * form the server rejects, for a value they never typed.
   */
  const availableDomains = useMemo(() => {
    const claimed = new Set(claimedDomains.map(d => d.trim().toLowerCase()))
    return allowedDomains.filter(d => !claimed.has(d.trim().toLowerCase()))
  }, [allowedDomains, claimedDomains])

  const availableDomainsRef = useRef(availableDomains)
  availableDomainsRef.current = availableDomains

  useEffect(() => {
    if (!isOpen) return

    // Clear everything the previous provider left behind, then seed for the new one.
    //
    // The modal stays mounted while authType changes, and react-hook-form keeps values for
    // unmounted fields by default (shouldUnregister is false). clientId and clientSecret are
    // shared across both providers, so without this reset a Microsoft client ID typed and then
    // abandoned would appear pre-filled in the Google Workspace form. The domain list has the
    // same problem in the other direction: Microsoft's prefilled sign-in domains would follow the
    // user into the Google flow and submit as aliases they never chose.
    const domains = availableDomainsRef.current

    reset({
      clientId: '',
      clientSecret: '',
      tenantDomain: '',
      // Pre-fill the Google domain when there is only one to choose
      domain: authType === AUTH_TYPE.GoogleWorkspace && domains.length === 1 ? domains[0] : ''
    })
    setDomainAliases(authType === AUTH_TYPE.Microsoft ? domains : [])
    setNewAlias('')
    setAddUnlistedDomains(false)
  }, [isOpen, authType, reset])

  const watchedGoogleDomain = watch('domain')

  /**
   * Domains this submission would use that the company does not yet claim.
   *
   * Computed here rather than discovered from a rejected request: the modal already has
   * `allowedDomains`, so the gap can be shown the moment a domain is entered instead of after a
   * round trip that discards a half-filled credentials form — including the client secret, which
   * Microsoft only shows once.
   *
   * Only the domains the server actually checks are considered. For Microsoft that is every
   * sign-in domain; for Google Workspace it is the primary domain, since its aliases are checked
   * for collisions between connections but not against the company's allowed list.
   */
  const unlistedDomains = useMemo(() => {
    const allowed = new Set(allowedDomains.map(d => d.trim().toLowerCase()).filter(Boolean))
    const candidates = isMicrosoft
      ? domainAliases
      : watchedGoogleDomain ? [watchedGoogleDomain] : []

    return [...new Set(
      candidates
        .map(d => d.trim().toLowerCase())
        .filter(d => d && !allowed.has(d))
    )]
  }, [isMicrosoft, domainAliases, watchedGoogleDomain, allowedDomains])

  // Clear the "Copied" timer on unmount so it cannot fire against an unmounted component.
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    }
  }, [])

  const resetFormState = () => {
    reset()
    setDomainAliases([])
    setNewAlias('')
    setAddUnlistedDomains(false)
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
    setCopied(false)
    setErrorMessages(null)
    setLoading(false)
  }

  const copyCallbackUrl = async () => {
    if (!AUTH0_CALLBACK_URL) return
    try {
      await navigator.clipboard.writeText(AUTH0_CALLBACK_URL)
      setCopied(true)
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current)
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be unavailable (insecure context, permissions). The URL is on screen and
      // selectable, so failing to copy is not worth surfacing as an error.
    }
  }

  const handleClose = () => {
    if (!loading) {
      resetFormState()
      onClose()
    }
  }

  const domainListLabel = isMicrosoft ? 'sign-in domain' : 'domain alias'

  const validateDomainAliases = (): string | null => {
    // Microsoft cannot be submitted without at least one sign-in domain. Auth0 will accept a
    // connection carrying only a tenant domain, and it then matches no real user at sign-in.
    if (isMicrosoft && domainAliases.length === 0) {
      return 'At least one sign-in domain is required. Add the domain your people\'s email addresses end in, e.g. contoso.com.'
    }
    for (const alias of domainAliases) {
      if (alias.length < 3 || alias.length > 253) {
        return `Each ${domainListLabel} must be between 3 and 253 characters`
      }
      if (!alias.includes('.')) {
        return `Each ${domainListLabel} must contain a dot (.)`
      }
    }
    return null
  }

  const onSubmit = async (data: AuthCredentialsFormData) => {
    setErrorMessages(null)

    const aliasError = validateDomainAliases()
    if (aliasError) {
      setErrorMessages([aliasError])
      return
    }

    // The server refuses a domain the company does not claim. Refuse it here too rather than
    // letting the round trip discard the form, and only widen the company when explicitly asked.
    if (unlistedDomains.length > 0 && (!addUnlistedDomains || !onAddAllowedDomains)) {
      setErrorMessages([
        `${unlistedDomains.join(', ')} ${unlistedDomains.length === 1 ? 'is' : 'are'} not yet ` +
        `${unlistedDomains.length === 1 ? 'a domain' : 'domains'} of this company. ` +
        (onAddAllowedDomains
          ? 'Tick "Also add to the company\'s allowed domains" to add it, or remove it above.'
          : 'Add it under Allowed Domains on this screen, or remove it above.')
      ])
      return
    }

    let request: AddAuthConnectionRequest

    if (isMicrosoft) {
      const credentials: MicrosoftCredentials = {
        clientId: data.clientId.trim(),
        clientSecret: data.clientSecret.trim(),
        tenantDomain: data.tenantDomain.trim(),
        emailDomains: domainAliases
      }
      request = { type: AUTH_TYPE.Microsoft, microsoft: credentials }
    } else {
      const credentials: GoogleWorkspaceCredentials = {
        clientId: data.clientId.trim(),
        clientSecret: data.clientSecret.trim(),
        domain: data.domain.trim(),
        domainAliases: domainAliases.length > 0 ? domainAliases : undefined
      }
      request = { type: AUTH_TYPE.GoogleWorkspace, googleWorkspace: credentials }
    }

    setLoading(true)
    try {
      // Widen the company first, deliberately. A domain can be refused because another company
      // already holds it, and discovering that after the Auth0 connection existed would leave the
      // organization half-configured with nothing to point at.
      if (unlistedDomains.length > 0 && onAddAllowedDomains) {
        await onAddAllowedDomains(unlistedDomains)
      }

      await onCreate(request)
      resetFormState()
      onClose()
    } catch (err) {
      setErrorMessages(getDisplayErrorMessages(err, 'Failed to create connection'))
    } finally {
      setLoading(false)
    }
  }

  const handleCreateSimple = async () => {
    setErrorMessages(null)
    setLoading(true)
    try {
      await onCreate({ type: authType! })
      resetFormState()
      onClose()
    } catch (err) {
      setErrorMessages(getDisplayErrorMessages(err, 'Failed to create connection'))
    } finally {
      setLoading(false)
    }
  }

  const addDomainAlias = () => {
    // Domains are case-insensitive, so normalize on the way in. Without this, "Contoso.com" and
    // "contoso.com" are two entries here and one domain everywhere else — the API lowercases them
    // before sending to Auth0, so the duplicate would only ever be visible in this list.
    const normalized = newAlias.trim().toLowerCase()
    if (normalized && !domainAliases.some(d => d.toLowerCase() === normalized)) {
      setDomainAliases([...domainAliases, normalized])
      setNewAlias('')
    }
  }

  const removeDomainAlias = (alias: string) => {
    setDomainAliases(domainAliases.filter(a => a !== alias))
  }

  if (!isOpen || authType === null) return null

  const displayLabel = typeLabel || authType

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Configure {displayLabel}
          </h3>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {errorMessages && errorMessages.length > 0 && (
            <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                {errorMessages.map(message => <p key={message}>{message}</p>)}
              </div>
            </div>
          )}

          {!requiresCredentials && (
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {authType === AUTH_TYPE.GoogleSocial ? (
                  <>No configuration required. Click <strong>Enable</strong> to allow users to sign in with any Google account.</>
                ) : (
                  <>No configuration required. Click <strong>Enable</strong> to allow email and password authentication. You can customize branding in the next step.</>
                )}
              </p>
            </div>
          )}

          {authType === AUTH_TYPE.GoogleWorkspace && (
            <GoogleWorkspaceCredentialFields
              register={register}
              errors={errors}
              loading={loading}
              allowedDomains={availableDomains}
            />
          )}

          {isMicrosoft && (
            <MicrosoftCredentialFields
              register={register}
              errors={errors}
              loading={loading}
              copied={copied}
              onCopyCallbackUrl={copyCallbackUrl}
            />
          )}

          {requiresCredentials && (
            <DomainListEditor
              isMicrosoft={isMicrosoft}
              loading={loading}
              domains={domainAliases}
              newDomain={newAlias}
              onNewDomainChange={setNewAlias}
              onAdd={addDomainAlias}
              onRemove={removeDomainAlias}
            />
          )}

          {requiresCredentials && unlistedDomains.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-200">
              <p>
                <strong>{unlistedDomains.join(', ')}</strong>{' '}
                {unlistedDomains.length === 1 ? 'is not one of' : 'are not among'} this company's
                domains yet.
              </p>
              {onAddAllowedDomains ? (
                <label className="mt-2 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addUnlistedDomains}
                    onChange={e => setAddUnlistedDomains((e.target as HTMLInputElement).checked)}
                    disabled={loading}
                    className="mt-0.5"
                  />
                  <span>
                    Also add {unlistedDomains.length === 1 ? 'it' : 'them'} to the company's allowed
                    domains.
                    <span className="block mt-1 text-xs opacity-90">
                      This also controls who can be invited to this company, and a domain can belong
                      to only one company.
                    </span>
                  </span>
                </label>
              ) : (
                <p className="mt-1 text-xs">
                  Add {unlistedDomains.length === 1 ? 'it' : 'them'} under Allowed Domains on this
                  screen, or remove {unlistedDomains.length === 1 ? 'it' : 'them'} above.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={requiresCredentials ? handleSubmit(onSubmit) : handleCreateSimple}
            disabled={loading}
            className="px-5 py-2 text-sm font-semibold rounded-lg bg-primary-600 hover:bg-primary-700 text-white flex items-center gap-2 disabled:opacity-50"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {requiresCredentials ? 'Create Connection' : 'Enable Method'}
          </button>
        </div>
      </div>
    </div>
  )
}
